import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  Paper,
  Box,
  InputLabel,
  Select,
  FormControl,
  Card,
  Avatar,
  IconButton,
  FormHelperText,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Pagination,
} from "@mui/material";
import { FirstPage, ChevronLeft, ChevronRight, LastPage, Edit } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { useQuery } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import handleExport from "utils/exportData";
import { IconTableExport } from "@tabler/icons-react";
import moment from "moment";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDAutocomplete from "components/MDAutocomplete";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import { convertUtcToIST } from "utils/ISTConvert";
import TicketCard from "./TicketCard";
import CloseIcon from "@mui/icons-material/Close";

const TicketList = () => {
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [selectedSort, setSelectedSort] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 9,
  });
  const [filters, setFilters] = useState({
    txtUserName: "",
    txtDistrict: "",
    txtPriority: "",
    txtStatus: "",
  });
  const priorities = [
    { title: "Urgent", value: "Urgent" },
    { title: "Normal", value: "Normal" },
    { title: "Medium", value: "Medium" },
  ];
  const statuses = [
    { title: "Open", value: "Open" },
    { title: "In Progress", value: "In Progress" },
    { title: "Closed", value: "Closed" },
  ];
  const sortOptions = [
    { label: "Ticket ID - ASC", id: "id", desc: false },
    { label: "Ticket ID - DESC", id: "id", desc: true },
    { label: "Status - ASC", id: "status", desc: false },
    { label: "Status - DESC", id: "status", desc: true },
    { label: "Priority - ASC", id: "prioritization", desc: false },
    { label: "Priority - DESC", id: "prioritization", desc: true },
  ];
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [exportData, setExportData] = useState([]);
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const navigate = useNavigate();
  const location = useLocation();

  const fetchDistrict = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/districts/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: districtData,
    error: districtError,
    isLoading: districtIsLoading,
  } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  const fetchAllUsers = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/role3`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: ["fetchAllUsers"],
    queryFn: fetchAllUsers,
    retry: false,
  });

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  useEffect(() => {
    if (meData) {
      fetchUsers(
        pagination.pageIndex,
        pagination.pageSize,
        filters,
        sorting,
        columnFilters,
        meData
      );
    }
  }, [pagination, sorting, columnFilters, meData]);

  // Handle filter from navigation state (Dashboard shortcut)
  useEffect(() => {
    if (location.state?.txtStatus && meData) {
      const statusValue = location.state.txtStatus;
      const updatedFilters = { ...filters, txtStatus: statusValue };
      setFilters(updatedFilters);
      fetchUsers(0, pagination.pageSize, updatedFilters, sorting, columnFilters, meData);
      // Clear state to avoid re-applying on every render or back navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, meData]);

  const fetchUsers = async (
    pageIndex = 0,
    pageSize = 9,
    filterValues = filters,
    sortValues = sorting,
    columnFilters = [],
    meData
  ) => {
    if (!meData) return;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
        columnFilters: JSON.stringify(columnFilters),
        txtUserName: filterValues.txtUserName,
        txtDistrict: filterValues.txtDistrict,
        txtPriority: filterValues.txtPriority,
        txtStatus: filterValues.txtStatus,
        user_id: meData.user_id,
        role_id: meData.role_id,
      });

      // Append sorting info
      if (sortValues.length > 0) {
        const sortField = sortValues[0].id;
        const sortOrder = sortValues[0].desc ? "desc" : "asc";
        queryParams.append("sortField", sortField);
        queryParams.append("sortOrder", sortOrder);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/tickets/list?${queryParams.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (res.data.status === 200) {
        setData(res.data.data);
        setRowCount(res.data.totalCount ?? 0);
        setExportData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
    setLoading(false);
  };

  const handleExportData = async () => {
    try {
      let formattedData = [];

      if (rowSelection && Object.keys(rowSelection).length > 0) {
        formattedData = exportData.filter((item) => rowSelection[item.id]);
      } else {
        const queryParams = new URLSearchParams({
          txtUserName: filters.txtUserName,
          txtDistrict: filters.txtDistrict,
          txtPriority: filters.txtPriority,
          txtStatus: filters.txtStatus,
          user_id: meData.user_id,
          role_id: meData.role_id,
          page: 1,
          limit: 10000,
        });

        if (sorting.length > 0) {
          const sortField = sorting[0].id;
          const sortOrder = sorting[0].desc ? "desc" : "asc";
          queryParams.append("sortField", sortField);
          queryParams.append("sortOrder", sortOrder);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/tickets/list?${queryParams.toString()}`,
          {
            withCredentials: true,
          }
        );

        if (res.data.status === 200) {
          formattedData = res.data.data;
        }
      }

      if (formattedData.length === 0) {
        console.log("No data found to export");
        setMessage({
          status: "error",
          title: "Error",
          msg: "No data found to export.",
        });
        return;
      }

      await handleExport(formattedData, "Tickets_List_Data");

      setMessage({
        status: "success",
        title: "Success",
        msg: "Excel downloaded successfully!",
      });
    } catch (error) {
      console.error(" Export error:", error);
      setMessage({
        status: "error",
        title: "Error",
        msg: "Something went wrong during export.",
      });
    }
  };

  const handleSortChange = (event) => {
    const selected = sortOptions.find((opt) => opt.label === event.target.value);
    setSelectedSort(event.target.value);
    if (selected) {
      setSorting([{ id: selected.id, desc: selected.desc }]);
    } else {
      setSorting([]);
    }
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} />
      <Card id="delete-account" sx={{ mb: 2 }}>
        <MDBox pt={2} px={2} pb={3}>
          {/* <MDBox mt={5} mb={3}> */}
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <MDTypography variant="h5" fontWeight="medium">
                Ticket List
              </MDTypography>
            </Grid>
            {meData?.role_id === 3 && (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} display="flex" justifyContent="flex-end">
                <MDButton
                  color={"dark"}
                  onClick={() => {
                    navigate("/ticket-add");
                  }}
                  sx={{
                    color: "white !important",
                    backgroundColor: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#155fa0",
                    },
                    minWidth: "120px",
                  }}
                >
                  + Create Ticket
                </MDButton>
              </Grid>
            )}
          </Grid>
          {/* </MDBox> */}
        </MDBox>
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            {meData?.role_id !== 3 && (
              <>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="Users"
                    name="txtUserName"
                    value={filters.txtUserName}
                    options={usersData}
                    onChange={(newValue) => {
                      setFilters({ ...filters, txtUserName: newValue });
                    }}
                    isFilter={true} // optional: add a flag if you need special styling
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="District"
                    name="txtDistrict"
                    value={filters.txtDistrict}
                    options={districtData}
                    onChange={(newValue) => {
                      setFilters({ ...filters, txtDistrict: newValue });
                    }}
                    isFilter={true} // optional: add a flag if you need special styling
                  />
                </Grid>
              </>
            )}
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Prioritization"
                name="txtPriority"
                value={filters.txtPriority}
                options={priorities}
                onChange={(newValue) => {
                  setFilters({ ...filters, txtPriority: newValue });
                }}
                isFilter={true}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Status"
                name="txtStatus"
                value={filters.txtStatus}
                options={statuses}
                onChange={(newValue) => {
                  setFilters({ ...filters, txtStatus: newValue });
                }}
                isFilter={true}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            {/* Search Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={() =>
                  fetchUsers(0, pagination.pageSize, filters, sorting, columnFilters, meData)
                }
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#155fa0",
                  },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>

            {/* Cancel Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={() => {
                  const cleared = {
                    txtUserName: "",
                    txtDistrict: "",
                    txtPriority: "",
                    txtStatus: "",
                  };
                  setFilters(cleared);
                  fetchUsers(0, pagination.pageSize, cleared, sorting, columnFilters, meData);
                }}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
      <MDBox px={1.5} py={1}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            p: "1rem",
          }}
        >
          <FormControl
            sx={{
              minWidth: 220,
              height: "48px", // Controls the overall height
            }}
          >
            <InputLabel sx={{ fontSize: "16px" }}>Sort By</InputLabel>
            <Select
              value={selectedSort}
              label="Sort By"
              onChange={handleSortChange}
              displayEmpty
              sx={{
                height: "48px", // Controls dropdown height
                fontSize: "16px",
                padding: "10px",
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              endAdornment={
                selectedSort && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent dropdown toggle
                      setSelectedSort("");
                      setSorting([]); // Clear sorting
                      fetchUsers(0, pagination.pageSize, filters, [], columnFilters, meData); // reset
                    }}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 10,
                      zIndex: 10,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Export data to excel sheet" arrow>
            <Button
              onClick={handleExportData}
              sx={{
                background:
                  "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%, rgb(110, 199, 235) 100%)",
                color: "#f4f4f4",
                borderRadius: "8px",
                padding: "10px 20px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                transition: "background 0.3s, transform 0.3s",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%, rgb(39, 169, 220) 100%)",
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
              startIcon={<IconTableExport />}
            >
              Export To Excel
            </Button>
          </Tooltip>
        </Box>
        {data.length === 0 ? (
          <Typography>No tickets found.</Typography>
        ) : (
          <>
            <Grid container spacing={4} alignItems="stretch">
              {data.map((ticket) => (
                <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={ticket.id}>
                  <TicketCard
                    ticket={ticket}
                    meData={meData}
                    usersData={usersData}
                    districtData={districtData}
                  />
                </Grid>
              ))}
            </Grid>
            <Box display="flex" justifyContent="center" width="100%" mt={2}>
              <Pagination
                count={Math.max(1, Math.ceil(rowCount / pagination.pageSize))}
                page={pagination.pageIndex + 1} // make sure it's 1-indexed
                onChange={(event, value) => {
                  setPagination((prev) => ({ ...prev, pageIndex: value - 1 }));
                }}
                color="primary"
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
                siblingCount={0} // show fewer siblings (or try 1)
                boundaryCount={1} // force boundary numbers to show
              />
            </Box>
          </>
        )}
      </MDBox>
      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          severity={message.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default TicketList;
